// src/pages/admin/CategoriesManage.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Typography, Modal, Form, Input as AntInput } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title } = Typography;

const CategoriesManage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [form] = Form.useForm();

  // Simulated API call to fetch categories
  const fetchCategories = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const mockData = [
        {
          id: 1,
          name: 'AI',
          description: 'Artificial Intelligence related topics',
          created_at: '2025-01-01T10:00:00Z',
          updated_at: '2025-01-01T10:00:00Z',
        },
        {
          id: 2,
          name: 'Web Development',
          description: 'Building web applications',
          created_at: '2025-02-01T09:00:00Z',
          updated_at: '2025-03-01T12:00:00Z',
        },
        // Add more mock data as needed
      ].filter(category =>
        category.name.toLowerCase().includes(search.toLowerCase()) ||
        (category.description && category.description.toLowerCase().includes(search.toLowerCase()))
      );

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedData = mockData.slice(start, end);

      setCategories(paginatedData);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: mockData.length,
      });
    } catch (error) {
      message.error('Failed to fetch categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(pagination.current, pagination.pageSize, searchText);
  }, [pagination.current, pagination.pageSize, searchText]);

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id },
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)}>View</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Popconfirm
            title="Are you sure to delete this category?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleView = (category) => {
    setSelectedCategory(category);
    setViewModalVisible(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    form.setFieldsValue(category); // Pre-fill form with category data
    setEditModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const updatedCategories = categories.filter(category => category.id !== id);
      setCategories(updatedCategories);
      message.success('Category deleted successfully');
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to delete category');
      console.error(error);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchCategories(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleCreateSubmit = async (values) => {
    try {
      const newCategory = {
        id: categories.length + 1,
        ...values,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setCategories([...categories, newCategory]);
      message.success('Category created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to create category');
      console.error(error);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      const updatedCategories = categories.map(category =>
        category.id === selectedCategory.id ? { ...category, ...values, updated_at: new Date().toISOString() } : category
      );
      setCategories(updatedCategories);
      message.success('Category updated successfully');
      setEditModalVisible(false);
      form.resetFields();
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to update category');
      console.error(error);
    }
  };

  const categoryFormFields = (
    <>
      <Form.Item
        name="name"
        label="Category Name"
        rules={[
          { required: true, message: 'Please enter the category name' },
          { max: 100, message: 'Name must not exceed 100 characters' },
          {
            validator: (_, value) =>
              categories.every(cat => cat.id === selectedCategory?.id || cat.name !== value)
                ? Promise.resolve()
                : Promise.reject('Category name must be unique'),
          },
        ]}
      >
        <AntInput placeholder="e.g., AI" />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <AntInput.TextArea placeholder="e.g., Artificial Intelligence related topics" rows={4} />
      </Form.Item>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={2} style={{ fontSize: '15px' }}>Manage Categories</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input.Search
            placeholder="Search categories by name or description"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            enterButton
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add New Category
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={categories}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} categories`,
        }}
        onChange={handleTableChange}
      />

      {/* View Modal */}
      <Modal
        title="Category Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={<Button onClick={() => setViewModalVisible(false)}>Close</Button>}
        width={600}
      >
        {selectedCategory && (
          <div>
            <p><strong>Id:</strong> {selectedCategory.id}</p>
            <p><strong>Name:</strong> {selectedCategory.name}</p>
            <p><strong>Description:</strong> {selectedCategory.description || 'N/A'}</p>
            <p><strong>Created At:</strong> {new Date(selectedCategory.created_at).toLocaleString()}</p>
            <p><strong>Updated At:</strong> {new Date(selectedCategory.updated_at).toLocaleString()}</p>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        title="Add New Category"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {categoryFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Create Category</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Category"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          {categoryFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Update Category</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setEditModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoriesManage;